import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Calendar, Trash2, Plus, HardDrive, Loader, MessageSquare, Instagram, AlertCircle, Edit, ChevronLeft, ChevronRight, Info, Lightbulb, Download, GitMerge, X, Copy, Settings } from 'lucide-react';
import { useStore } from '../store';
import { useArtistClient } from '../contexts/ArtistClientContext';
import { ArtistSettingsModal } from '../components/ArtistSettingsModal';
import { AdCreativeSubmissionModal } from '../components/AdCreativeSubmissionModal';
import { EditAdCreativeModal } from '../components/EditAdCreativeModal';
import { BulkAdCreativeReleaseModal } from '../components/BulkAdCreativeReleaseModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { VideoPreviewModal } from '../components/VideoPreviewModal';
import { RejectionReasonTag } from '../components/RejectionReasonTag';
import { ReleaseAssetsModal } from '../components/ReleaseAssetsModal';
import { format } from 'date-fns';
import { isSupabaseStorageUrl } from '../utils/video/player';
import { fetchInstagramThumbnail } from '../utils/fetchInstagramThumbnail';
import { generateThumbnailFromUrl, updateAdCreativeThumbnail } from '../utils/video/generateThumbnailFromUrl';
import { detectDuplicateCreatives, mergeCreatives } from '../utils/compareThumbnails';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export const ArtistAdCreatives: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { artists, adCreatives, fetchAdCreatives, fetchArtists, deleteAdCreative, loading } = useStore();
  const { client: artistClient, isProtected, loading: clientLoading } = useArtistClient();
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  // Release cover/Spotify-URI quick modal (opened from release group headers)
  const [assetRelease, setAssetRelease] = useState<any | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCreatives, setSelectedCreatives] = useState<Set<string>>(new Set());
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    videoUrl: string;
    videoName?: string;
    platform: string;
    creativeId?: string;
  }>({
    isOpen: false,
    videoUrl: '',
    videoName: '',
    platform: '',
    creativeId: ''
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    creativeId: string | null;
  }>({
    isOpen: false,
    creativeId: null,
  });
  const [multiDeleteModal, setMultiDeleteModal] = useState<{
    isOpen: boolean;
    count: number;
  }>({
    isOpen: false,
    count: 0,
  });
  const [multiDeleteConfirmInput, setMultiDeleteConfirmInput] = useState('');
  const [isMultiDeleting, setIsMultiDeleting] = useState(false);
  const [editingCreative, setEditingCreative] = useState<any>(null);
  const [showBulkReleaseModal, setShowBulkReleaseModal] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Duplicate detection and merge state
  const [duplicateMatches, setDuplicateMatches] = useState<any[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isDetectingDuplicates, setIsDetectingDuplicates] = useState(false);

  // True when any modal is currently open. Background edge-function
  // refreshes defer until this is false so they don't interrupt the user
  // (e.g., re-rendering the table while the preview modal is mid-view).
  const isAnyModalOpen =
    previewModal.isOpen ||
    showSubmissionModal ||
    showBulkReleaseModal ||
    !!editingCreative ||
    deleteConfirmation.isOpen ||
    multiDeleteModal.isOpen ||
    showDuplicateModal;
  const pendingRefreshRef = useRef(false);
  
  const isProcessingAudio = useRef(false);
  const processedCreatives = useRef<Set<string>>(new Set());
  const processedVideoId = useRef<string | null>(null);
  
  const artist = artists.find(a => a.id === id);

  // Update document title with artist name
  useEffect(() => {
    if (artist) {
      document.title = `${artist.name} | SwipeUp Tool`;
    }
    return () => {
      document.title = 'SwipeOS';
    };
  }, [artist]);

  // Function to fetch artist-specific ad creatives
  const fetchArtistAdCreatives = useCallback(async () => {
    if (id) {
      console.log('ArtistAdCreatives: Fetching data for artist ID:', id);
      const filters = {
        artistId: id,
        status: undefined // Don't filter by status initially to see all creatives
      };
      await fetchAdCreatives(1, 200, filters, false, isProtected ? artistClient : undefined);
    }
  }, [id, fetchAdCreatives, isProtected, artistClient]);

  // Guarded refresh: only hit the network (with cache skip) when no modal
  // is open. If a modal is currently open, stash a pending flag and the
  // effect below flushes it the moment the modal closes. This prevents
  // edge-function completions from triggering a table re-render while
  // the user is interacting with preview/edit/submission/bulk modals.
  const refreshIfIdle = useCallback(() => {
    if (!id) return;
    if (isAnyModalOpen) {
      pendingRefreshRef.current = true;
      return;
    }
    fetchAdCreatives(1, 200, { artistId: id, status: undefined }, true, isProtected ? artistClient : undefined).catch(
      console.error,
    );
  }, [id, isAnyModalOpen, fetchAdCreatives, isProtected, artistClient]);

  // Flush a deferred refresh whenever the last modal closes.
  useEffect(() => {
    if (!isAnyModalOpen && pendingRefreshRef.current && id) {
      pendingRefreshRef.current = false;
      console.log('🔄 ArtistAdCreatives: flushing deferred refresh after modal close');
      fetchAdCreatives(1, 200, { artistId: id, status: undefined }, true, isProtected ? artistClient : undefined).catch(
        console.error,
      );
    }
  }, [isAnyModalOpen, id, fetchAdCreatives, isProtected, artistClient]);

  useEffect(() => {
    if (clientLoading) return;
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await Promise.all([
          fetchArtistAdCreatives(),
          fetchArtists(isProtected ? artistClient : undefined)
        ]);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading ad creatives:', err);
        setError('Failed to load ad creatives. Please try refreshing the page.');
        setIsLoading(false);
      }
    };
    loadData();
  }, [fetchArtistAdCreatives, fetchArtists, clientLoading]);

  // Check for video_id in URL and auto-open modal
  useEffect(() => {
    const videoId = searchParams.get('video_id');
    if (videoId && adCreatives.length > 0 && !previewModal.isOpen && processedVideoId.current !== videoId) {
      const creative = adCreatives.find(c => String(c.id) === String(videoId));
      if (creative) {
        // Mark this video_id as processed
        processedVideoId.current = videoId;
        // Directly set the modal state without adding video_id to URL again
        setPreviewModal({
          isOpen: true,
          videoUrl: (creative.platform === 'tiktok' || creative.content?.startsWith('#')) ? '' : creative.content, // Don't pass auth code for TikTok
          videoName: creative.videoName,
          platform: creative.platform,
          creativeId: creative.id,
        });
      }
    }
  }, [searchParams.toString(), adCreatives, previewModal.isOpen, setPreviewModal]);

  const filteredCreatives = useMemo(() => {
    const filtered = adCreatives
      .filter(creative => {
        const belongsToArtist = String(creative.artists_id) === String(id);
        const notArchived = creative.status !== 'archived';
        return belongsToArtist && notArchived;
      })
      // Primary: release_date desc (newest release first; null releases last).
      // Secondary: release_id (groups creatives of the same release together
      //            even when multiple releases share the same release_date —
      //            e.g. regional duplicates of one campaign).
      // Tertiary: release_name (stable order within the same date bucket).
      // Quaternary: createdAt desc within the same release.
      // This must run BEFORE pagination so the latest release's creatives
      // land on page 1 even if their createdAt is older than creatives
      // belonging to older releases.
      .sort((a, b) => {
        const aRelDate = (a as any).releases?.release_date;
        const bRelDate = (b as any).releases?.release_date;
        const aRelId = (a as any).release_id || null;
        const bRelId = (b as any).release_id || null;
        const aRelName = (a as any).releases?.name || '';
        const bRelName = (b as any).releases?.name || '';
        if (aRelId && !bRelId) return -1;
        if (!aRelId && bRelId) return 1;
        const aTime = aRelDate ? new Date(aRelDate).getTime() : 0;
        const bTime = bRelDate ? new Date(bRelDate).getTime() : 0;
        if (bTime !== aTime) return bTime - aTime;
        if (aRelId !== bRelId) {
          const nameCmp = aRelName.localeCompare(bRelName);
          if (nameCmp !== 0) return nameCmp;
          return String(aRelId).localeCompare(String(bRelId));
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    return filtered;
  }, [adCreatives, id]);

  // Create a flat list with divider markers (grouped by release)
  // Paginated creatives
  const paginatedCreatives = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCreatives.slice(startIndex, endIndex);
  }, [filteredCreatives, currentPage]);

  const totalPages = Math.ceil(filteredCreatives.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [id]);

  // Auto-scan Instagram creatives on the current page for missing audio_type and thumbnails
  useEffect(() => {
    console.log('🔍 Auto-scan useEffect triggered:', { id, paginatedCreativesLength: paginatedCreatives.length, isProcessing: isProcessingAudio.current, hasScanned: processedCreatives.current.has('scanPage') });
    
    const scanPageCreatives = async () => {
      if (!id || isProcessingAudio.current) return;
      isProcessingAudio.current = true;

      try {
        console.log('🔄 Auto-scanning Instagram creatives on current page for missing audio_type and thumbnails');
        console.log('📋 Total creatives on page:', paginatedCreatives.length);
        console.log('📋 Creatives breakdown:', paginatedCreatives.map(c => ({
          id: c.id,
          platform: c.platform,
          audio_type: c.audio_type,
          instagram_thumbnail_url: c.instagram_thumbnail_url,
          mergedInstagramReelUrl: c.mergedInstagramReelUrl
        })));
        
        // Scan all Instagram creatives on the page that have an Instagram URL
        const creativesToScan = paginatedCreatives.filter(c => 
          c.platform === 'instagram' && (c.mergedInstagramReelUrl || c.content)
        );

        console.log(`Found ${creativesToScan.length} Instagram creatives on current page with Instagram URL`);

        // Process one at a time to avoid rate limiting (3 requests per second limit)
        let licensedCount = 0;
        let originalCount = 0;
        let errorCount = 0;

        for (let i = 0; i < creativesToScan.length; i++) {
          const creative = creativesToScan[i];
          
          try {
            const result = await fetch('https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/scan-instagram-audio', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${(window as any).supabase?.auth?.session()?.access_token || ''}`
              },
              body: JSON.stringify({
                creative_id: creative.id,
                instagram_url: creative.mergedInstagramReelUrl || creative.content,
                video_name: creative.videoName,
                fetch_thumbnail_only: !creative.audio_type
              })
            }).then(res => res.json());

            if (result.audio_type === 'licensed') licensedCount++;
            else if (result.audio_type === 'original' || result.audio_type === 'original_sounds') originalCount++;
          } catch (error) {
            errorCount++;
          }

          // Add 1-second delay between each request to respect rate limit
          if (i < creativesToScan.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        console.log(`✅ Scanned ${creativesToScan.length} creatives: ${licensedCount} licensed, ${originalCount} original, ${errorCount} errors`);

        // Scan updates audio_type + (for licensed) content/merged_* URLs
        // via the backend. Refresh the list so the table reflects this —
        // but only when no modal is open (deferred otherwise).
        if (licensedCount > 0 || originalCount > 0) {
          refreshIfIdle();
        }

        if (licensedCount > 0) {
          toast.success(
            `${licensedCount} licensed reel${licensedCount === 1 ? '' : 's'} downloaded to storage`,
            { duration: 5000 }
          );
        }
      } catch (error) {
        console.error('Error auto-scanning Instagram creatives:', error);
      } finally {
        isProcessingAudio.current = false;
      }
    };

    // Auto-fetch TikTok Spark Ad thumbnails on page load. Calls
    // download-tiktok-spark-ad-media on the reporting project for each
    // TikTok auth-code row on the current page that still has no
    // tiktok_thumbnail_url (or whose URL points at the expiring TikTok
    // CDN). The edge function authorizes the code, fetches tt_video/info
    // and permanently mirrors the poster into our thumbnails bucket.
    const fetchMissingTikTokThumbnails = async () => {
      const isExpiringTiktokCdnUrl = (url: string | null | undefined) =>
        !!url && (url.includes('tiktokcdn.com') || url.includes('tiktokcdn-us.com') || url.includes('.tiktokv.com'));
      const targets = paginatedCreatives.filter((c: any) => {
        const isTiktok = c.platform === 'tiktok';
        const hasAuthCode =
          !!c.mergedTiktokAuthCode ||
          (typeof c.content === 'string' && c.content.startsWith('#'));
        const thumb = c.tiktok_thumbnail_url;
        const needsThumbnail = !thumb || isExpiringTiktokCdnUrl(thumb);
        const notProcessed = !processedCreatives.current.has(`tt_${c.id}`);
        return isTiktok && hasAuthCode && needsThumbnail && notProcessed;
      });
      if (targets.length === 0) return;
      console.log(`🎵 ArtistAdCreatives: fetching TikTok posters for ${targets.length} creative(s)`);
      const REPORTING_URL = 'https://uydhsjvwrgupgfjevqsz.supabase.co';
      const REPORTING_ANON =
        'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';
      for (const c of targets.slice(0, 10)) {
        try {
          await fetch(`${REPORTING_URL}/functions/v1/download-tiktok-spark-ad-media`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${REPORTING_ANON}`,
            },
            body: JSON.stringify({ creative_id: c.id, poster_only: true }),
          });
          processedCreatives.current.add(`tt_${c.id}`);
        } catch (err) {
          console.error('TikTok poster fetch failed for', c.id, err);
        }
        await new Promise((r) => setTimeout(r, 300));
      }
      // Refresh so newly stored tiktok_thumbnail_urls show up in the
      // table — but only if no modal is currently open (otherwise defer
      // until the user closes it).
      refreshIfIdle();
    };

    // Auto-fetch Instagram poster thumbnails on page load. Any creative
    // that carries an Instagram reel URL (regardless of platform column —
    // direct_upload rows that were mirrored from IG still need a poster)
    // but lacks `instagram_thumbnail_url` triggers fetch-instagram-thumbnail
    // on the main project. Admin view has the same logic; artist view
    // was previously missing it, so the list thumbnail column stayed a
    // grey placeholder until the user opened the VideoPreviewModal.
    const fetchMissingInstagramThumbnails = async () => {
      const targets = paginatedCreatives.filter((c: any) => {
        const hasIgUrl =
          !!c.mergedInstagramReelUrl ||
          !!c.original_instagram_url ||
          (typeof c.content === 'string' && c.content.includes('instagram.com'));
        const needsThumbnail = !c.instagram_thumbnail_url && !c.thumbnail_url;
        const notProcessed = !processedCreatives.current.has(`ig_${c.id}`);
        return hasIgUrl && needsThumbnail && notProcessed;
      });
      if (targets.length === 0) return;
      console.log(`🖼️ ArtistAdCreatives: fetching IG posters for ${targets.length} creative(s)`);
      for (const c of targets.slice(0, 10)) processedCreatives.current.add(`ig_${c.id}`);
      for (const c of targets.slice(0, 10)) {
        try {
          const url =
            c.mergedInstagramReelUrl ||
            c.original_instagram_url ||
            c.content;
          const thumbnailUrl = await fetchInstagramThumbnail(url, c.id);
          if (thumbnailUrl) {
            // Patch the single row in the store so the table updates live
            // without waiting for the full refresh.
            useStore.setState((state: any) => ({
              adCreatives: state.adCreatives.map((x: any) =>
                String(x.id) === String(c.id)
                  ? { ...x, instagram_thumbnail_url: thumbnailUrl }
                  : x,
              ),
            }));
          }
        } catch (err) {
          console.error('IG thumbnail fetch failed for', c.id, err);
        }
        await new Promise((r) => setTimeout(r, 300));
      }
      // Final modal-guarded refresh so any other server-side side effects
      // (audio_type, merged_* URLs) also sync to the list view.
      refreshIfIdle();
    };

    // Auto-generate thumbnails for direct_upload / dropbox / supabase-storage
    // creatives that are missing a thumbnail_url. Generates from the video
    // file in-browser, uploads to the thumbnails bucket, and patches the row.
    const fetchMissingDirectUploadThumbnails = async () => {
      const targets = paginatedCreatives.filter((c: any) => {
        const url = c.content;
        if (!url || typeof url !== 'string') return false;
        const isStorage = isSupabaseStorageUrl(url);
        const isDropbox = url.includes('dropbox.com') && url.includes('dl=1');
        if (!isStorage && !isDropbox) return false;
        const needsThumbnail = !c.thumbnail_url && !c.instagram_thumbnail_url && !c.tiktok_thumbnail_url;
        const notProcessed = !processedCreatives.current.has(`du_${c.id}`);
        return needsThumbnail && notProcessed;
      });
      if (targets.length === 0) return;
      console.log(`🖼️ ArtistAdCreatives: generating thumbnails for ${targets.length} direct-upload creative(s)`);
      for (const c of targets.slice(0, 10)) processedCreatives.current.add(`du_${c.id}`);
      for (const c of targets.slice(0, 10)) {
        try {
          const thumbUrl = await generateThumbnailFromUrl(c.content, isProtected ? artistClient : undefined);
          if (thumbUrl) {
            await updateAdCreativeThumbnail(String(c.id), thumbUrl, isProtected ? artistClient : undefined);
            useStore.setState((state: any) => ({
              adCreatives: state.adCreatives.map((x: any) =>
                String(x.id) === String(c.id)
                  ? { ...x, thumbnail_url: thumbUrl }
                  : x,
              ),
            }));
            console.log(`🖼️ ArtistAdCreatives: thumbnail generated for ${c.id}`);
          }
        } catch (err) {
          console.warn('Direct upload thumbnail generation failed for', c.id, err);
        }
        await new Promise((r) => setTimeout(r, 200));
      }
      refreshIfIdle();
    };

    // Trigger scan when artist is selected and data is loaded
    if (id && paginatedCreatives.length > 0 && !processedCreatives.current.has('scanPage')) {
      console.log('🚀 Starting scan for page');
      processedCreatives.current.add('scanPage');
      // Delay scan slightly to ensure UI renders first
      setTimeout(() => scanPageCreatives(), 500);
      // Kick off TikTok poster fetch in parallel (non-blocking); it's
      // independent of the IG audio scan and uses its own cooldown set.
      setTimeout(() => fetchMissingTikTokThumbnails(), 500);
      // Kick off IG poster fetch in parallel for rows missing a thumbnail.
      setTimeout(() => fetchMissingInstagramThumbnails(), 500);
      // Kick off direct-upload thumbnail generation for rows missing a thumbnail.
      setTimeout(() => fetchMissingDirectUploadThumbnails(), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, paginatedCreatives.length]);

  const creativesWithDividers = useMemo(() => {
    const releaseMap = new Map<string | null, any[]>();
    
    paginatedCreatives.forEach(creative => {
      const releaseId = (creative as any).release_id || null;
      if (!releaseMap.has(releaseId)) {
        releaseMap.set(releaseId, []);
      }
      releaseMap.get(releaseId)!.push(creative);
    });

    // Check if we should show dividers
    const shouldShowDividers = releaseMap.size > 1 || (releaseMap.size === 1 && !releaseMap.has(null));
    
    if (!shouldShowDividers) {
      return paginatedCreatives.map(c => ({ type: 'creative' as const, data: c }));
    }

    // Create sorted groups
    const groups: { releaseId: string | null; releaseName: string | null; releaseDate: string | null; creatives: any[] }[] = [];
    releaseMap.forEach((creatives, releaseId) => {
      groups.push({
        releaseId,
        releaseName: releaseId ? (creatives[0] as any).releases?.name : null,
        releaseDate: releaseId ? (creatives[0] as any).releases?.release_date : null,
        creatives
      });
    });

    // Sort: releases first by release_date descending (newest first), then "No Release"
    groups.sort((a, b) => {
      if (a.releaseId === null) return 1;
      if (b.releaseId === null) return -1;
      const aTime = a.releaseDate ? new Date(a.releaseDate).getTime() : 0;
      const bTime = b.releaseDate ? new Date(b.releaseDate).getTime() : 0;
      if (bTime !== aTime) return bTime - aTime;
      return (a.releaseName || '').localeCompare(b.releaseName || '');
    });

    // Flatten with dividers
    const result: Array<{ type: 'divider' | 'creative'; data: any; releaseName?: string }> = [];
    groups.forEach(group => {
      result.push({ type: 'divider', data: group, releaseName: group.releaseName || 'No Release' });
      group.creatives.forEach(creative => {
        result.push({ type: 'creative', data: creative });
      });
    });

    return result;
  }, [paginatedCreatives]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'archived':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'expired':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  // Creatives with expired TikTok auth codes have status="error" but
  // tiktok_ad_status="EXPIRED_AUTH_CODE". Show "Expired" instead of "Error".
  const getDisplayStatus = (creative: any): string => {
    if (creative.status === 'error' && creative.tiktok_ad_status === 'EXPIRED_AUTH_CODE') {
      return 'expired';
    }
    return creative.status;
  };

  const handleDelete = async () => {
    if (deleteConfirmation.creativeId) {
      try {
        setDeletingId(deleteConfirmation.creativeId);
        await deleteAdCreative(deleteConfirmation.creativeId, artistClient || undefined);
      } catch (error) {
        console.error('Failed to delete ad creative:', error);
      } finally {
        setDeletingId(null);
      }
    }
    setDeleteConfirmation({ isOpen: false, creativeId: null });
  };

  const handleMultiDelete = async () => {
    if (selectedCreatives.size === 0) return;
    
    try {
      setIsMultiDeleting(true);
      const deletePromises = Array.from(selectedCreatives).map(id => deleteAdCreative(id, artistClient || undefined));
      await Promise.all(deletePromises);
      setSelectedCreatives(new Set());
    } catch (error) {
      console.error('Failed to delete ad creatives:', error);
    } finally {
      setIsMultiDeleting(false);
      setMultiDeleteModal({ isOpen: false, count: 0 });
      setMultiDeleteConfirmInput('');
    }
  };

  const handleDeleteClick = (creativeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // If multiple items are selected and the clicked item is one of them, show multi-delete modal
    if (selectedCreatives.size > 1 && selectedCreatives.has(creativeId)) {
      setMultiDeleteModal({ isOpen: true, count: selectedCreatives.size });
    } else {
      // Single delete
      setDeleteConfirmation({ isOpen: true, creativeId });
    }
  };

  // Helper function to check if Instagram URL is new (added after last activation)
  const isInstagramUrlNew = (creative: any): boolean => {
    if (!creative.instagram_url_updated_at || !creative.mergedInstagramReelUrl) return false;
    if (!creative.last_activated_at) return false; // Never activated = not "new" (just not processed yet)
    return new Date(creative.instagram_url_updated_at) > new Date(creative.last_activated_at);
  };

  // Helper function to check if TikTok code is new (added after last activation)
  const isTikTokCodeNew = (creative: any): boolean => {
    if (!creative.tiktok_url_updated_at || !creative.mergedTiktokAuthCode) return false;
    if (!creative.last_activated_at) return false; // Never activated = not "new" (just not processed yet)
    return new Date(creative.tiktok_url_updated_at) > new Date(creative.last_activated_at);
  };

  const handleMergeCreatives = async (fileCreativeId: string, instagramCreativeId: string) => {
    console.log('🔄 Merge clicked - fileCreativeId:', fileCreativeId, 'instagramCreativeId:', instagramCreativeId);

    const fileCreative = adCreatives.find(c => c.id === fileCreativeId);
    const instagramCreative = adCreatives.find(c => c.id === instagramCreativeId);

    if (!fileCreative || !instagramCreative) {
      toast.error('One or both creatives not found');
      return;
    }

    const isFileTarget = fileCreative.platform === 'direct_upload' || fileCreative.platform === 'dropbox';

    if (!isFileTarget) {
      toast.error('Target must be a file upload (direct_upload or dropbox)');
      return;
    }

    try {
      await mergeCreatives(fileCreativeId, instagramCreativeId);
      toast.success('Creatives merged successfully');

      // Refresh the list
      await fetchArtistAdCreatives();

      // Close the modal
      setShowDuplicateModal(false);
      setDuplicateMatches([]);
      setSelectedCreatives(new Set());
    } catch (error: any) {
      console.error('Error merging creatives:', error);
      toast.error('Failed to merge creatives: ' + (error.message || 'Unknown error'));
    }
  };

  const handleMergeSelected = async () => {
    const selectedIds = Array.from(selectedCreatives);
    const selectedCreativesData = adCreatives.filter(c => selectedIds.includes(c.id));

    if (selectedCreativesData.length < 2 || selectedCreativesData.length > 3) {
      toast.error('Please select 2-3 creatives to merge');
      return;
    }

    try {
      const artistId = selectedCreativesData[0].artists_id;
      const matches = await detectDuplicateCreatives(artistId, selectedIds);

      const filteredMatches = matches.filter(match =>
        selectedIds.includes(match.fileCreative.id) && selectedIds.includes(match.instagramCreative.id)
      );

      setDuplicateMatches(filteredMatches);

      if (filteredMatches.length > 0) {
        toast.success(`Found ${filteredMatches.length} merge pair(s)`);
        setShowDuplicateModal(true);
      } else {
        // Create manual merge pairs
        const fileCreative = selectedCreativesData.find(c => c.platform === 'direct_upload' || c.platform === 'dropbox');
        const otherCreatives = selectedCreativesData.filter(c => c.id !== fileCreative?.id);

        if (!fileCreative) {
          toast.error('No file upload found in selection. Please select at least one file upload.');
          return;
        }

        const manualMatches = otherCreatives.map(creative => ({
          fileCreative,
          instagramCreative: creative,
          similarity: 0
        }));

        setDuplicateMatches(manualMatches);
        toast.success(`Created ${manualMatches.length} manual merge pair(s)`);
        setShowDuplicateModal(true);
      }
    } catch (error) {
      console.error('Error preparing merge:', error);
      toast.error('Failed to prepare merge');
    }
  };

  const handleMergeAll = async () => {
    if (duplicateMatches.length === 0) return;

    const fileCreativeId = duplicateMatches[0].fileCreative.id;
    const fileCreative = adCreatives.find(c => c.id === fileCreativeId);

    if (!fileCreative) {
      toast.error('File creative not found');
      return;
    }

    const isFileTarget = fileCreative.platform === 'direct_upload' || fileCreative.platform === 'dropbox';

    if (!isFileTarget) {
      toast.error('Merge All requires a file upload as the target');
      return;
    }

    try {
      for (const match of duplicateMatches) {
        await mergeCreatives(fileCreativeId, match.instagramCreative.id);
      }
      toast.success(`Merged ${duplicateMatches.length} creative(s) successfully`);

      await fetchArtistAdCreatives();
      setShowDuplicateModal(false);
      setDuplicateMatches([]);
      setSelectedCreatives(new Set());
    } catch (error: any) {
      console.error('Error merging all:', error);
      toast.error('Failed to merge all creatives: ' + (error.message || 'Unknown error'));
    }
  };

  const openPreviewForCreative = useCallback((creative: any) => {
    // VideoPreviewModal handles all platforms: direct_upload/dropbox play
    // the stored video, instagram shows the reel poster + link-out (or the
    // mirrored Supabase video if available), tiktok resolves the Spark Ad
    // preview via auth code.
    setPreviewModal({
      isOpen: true,
      videoUrl: (creative.platform === 'tiktok' || creative.content?.startsWith('#')) ? '' : creative.content, // Don't pass auth code for TikTok
      videoName: creative.videoName,
      platform: creative.platform,
      creativeId: creative.id,
    });
    // Add video_id to URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set('video_id', String(creative.id));
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleRowClick = (creative: any, event: React.MouseEvent) => {
    // Don't open preview if clicking on buttons or links
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.tagName === 'A') {
      return;
    }
    openPreviewForCreative(creative);
  };

  const handleMessageClick = (creative: any, event: React.MouseEvent) => {
    event.stopPropagation();
    openPreviewForCreative(creative);
  };
  
  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Error Loading Ad Creatives
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading || (!artist && artists.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading ad creatives...</p>
        </div>
      </div>
    );
  }
  
  if (!artist) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black p-4">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Artist not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The artist you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black">
      <header className="bg-primary-500 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col space-y-4">
            <div className="flex items-center space-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-6 w-6" fill="currentColor">
                <path d="M544 96C526.3 96 512 110.3 512 128L512 137.1L126.8 247.2C123 233.8 110.6 224 96 224C78.3 224 64 238.3 64 256L64 384C64 401.7 78.3 416 96 416C110.6 416 123 406.2 126.8 392.8L198.5 413.3C194.3 424.1 192 435.8 192 448C192 501 235 544 288 544C334.9 544 374 510.3 382.3 465.8L512 502.8L512 511.9C512 529.6 526.3 543.9 544 543.9C561.7 543.9 576 529.6 576 511.9L576 127.9C576 110.2 561.7 95.9 544 95.9zM335.8 452.5C333.5 476.9 313 496 288 496C261.5 496 240 474.5 240 448C240 440.3 241.8 433 245 426.6L335.8 452.5z"/>
              </svg>
              <span className="text-xl font-bold">Ad Creatives</span>
            </div>

            <div className="flex border-b border-white/20 justify-between">
              <div className="flex space-x-4">
                <Link
                  to={`/artist/${id}`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-5 w-5" fill="currentColor">
                      <path d="M96 160C96 124.7 124.7 96 160 96L480 96C515.3 96 544 124.7 544 160L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 160zM144 432L144 464C144 472.8 151.2 480 160 480L192 480C200.8 480 208 472.8 208 464L208 432C208 423.2 200.8 416 192 416L160 416C151.2 416 144 423.2 144 432zM448 416C439.2 416 432 423.2 432 432L432 464C432 472.8 439.2 480 448 480L480 480C488.8 480 496 472.8 496 464L496 432C496 423.2 488.8 416 480 416L448 416zM144 304L144 336C144 344.8 151.2 352 160 352L192 352C200.8 352 208 344.8 208 336L208 304C208 295.2 200.8 288 192 288L160 288C151.2 288 144 295.2 144 304zM448 288C439.2 288 432 295.2 432 304L432 336C432 344.8 439.2 352 448 352L480 352C488.8 352 496 344.8 496 336L496 304C496 295.2 488.8 288 480 288L448 288zM144 176L144 208C144 216.8 151.2 224 160 224L192 224C200.8 224 208 216.8 208 208L208 176C208 167.2 200.8 160 192 160L160 160C151.2 160 144 167.2 144 176zM448 160C439.2 160 432 167.2 432 176L432 208C432 216.8 439.2 224 448 224L480 224C488.8 224 496 216.8 496 208L496 176C496 167.2 488.8 160 480 160L448 160z"/>
                    </svg>
                    <span className="hidden sm:inline">Videos</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${id}/ad-creatives`}
                  className="pb-2 px-1 border-b-2 border-white text-white"
                >
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className="h-5 w-5" fill="currentColor">
                      <path d="M544 96C526.3 96 512 110.3 512 128L512 137.1L126.8 247.2C123 233.8 110.6 224 96 224C78.3 224 64 238.3 64 256L64 384C64 401.7 78.3 416 96 416C110.6 416 123 406.2 126.8 392.8L198.5 413.3C194.3 424.1 192 435.8 192 448C192 501 235 544 288 544C334.9 544 374 510.3 382.3 465.8L512 502.8L512 511.9C512 529.6 526.3 543.9 544 543.9C561.7 543.9 576 529.6 576 511.9L576 127.9C576 110.2 561.7 95.9 544 95.9zM335.8 452.5C333.5 476.9 313 496 288 496C261.5 496 240 474.5 240 448C240 440.3 241.8 433 245 426.6L335.8 452.5z"/>
                    </svg>
                    <span className="hidden sm:inline">Ad Creatives</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${id}/content-plan`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5" />
                    <span className="hidden sm:inline">Content Plan</span>
                  </div>
                </Link>
                <Link
                  to={`/artist/${id}/ideas`}
                  className="pb-2 px-1 text-white/70 hover:text-white"
                >
                  <div className="flex items-center space-x-2">
                    <Lightbulb className="h-5 w-5" />
                    <span className="hidden sm:inline">Ideas</span>
                  </div>
                </Link>
              </div>

              <button
                onClick={() => setShowSettings(true)}
                className="text-white/70 hover:text-white transition-colors pb-2"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="space-y-4 sm:space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold dark:text-white">
                {artist.name}'s Ad Creatives ({filteredCreatives.length})
              </h2>
              <p className="text-gray-600 dark:text-gray-400">Manage Instagram Reels and TikTok URLs</p>
              {totalPages > 1 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Page {currentPage} of {totalPages}
                </p>
              )}
            </div>
            <button onClick={() => setShowSubmissionModal(true)} className="btn">
              <Plus className="h-5 w-5 sm:mr-2" />
              <span className="hidden sm:inline">Add URLs</span>
            </button>
          </div>

          {/* Bulk Action Bar - shown when multiple creatives are selected */}
          {selectedCreatives.size > 1 && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-gray-300">
                {selectedCreatives.size} ad creative{selectedCreatives.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                {selectedCreatives.size >= 2 && selectedCreatives.size <= 3 && (
                  <button
                    onClick={handleMergeSelected}
                    className="btn btn-sm flex items-center gap-2"
                  >
                    <GitMerge className="h-4 w-4" />
                    Merge
                  </button>
                )}
                <button
                  onClick={() => setShowBulkReleaseModal(true)}
                  className="btn btn-sm flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Assign Release
                </button>
                <button
                  onClick={() => setMultiDeleteModal({ isOpen: true, count: selectedCreatives.size })}
                  className="btn btn-sm bg-red-600 hover:bg-red-700 flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Selected
                </button>
                <button
                  onClick={() => setSelectedCreatives(new Set())}
                  className="text-gray-400 hover:text-gray-200 text-sm"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={filteredCreatives.length > 0 && selectedCreatives.size === filteredCreatives.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCreatives(new Set(filteredCreatives.map(c => String(c.id))));
                          } else {
                            setSelectedCreatives(new Set());
                          }
                        }}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Thumbnail
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Content
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredCreatives.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No ad creatives found
                      </td>
                    </tr>
                  ) : (
                    creativesWithDividers.map((item, index) => {
                      if (item.type === 'divider') {
                        return (
                          <tr key={`divider-${index}`}>
                            <td colSpan={7} className="px-6 py-2" style={{ backgroundColor: '#0000fe' }}>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-white/50 rounded cursor-pointer"
                                  checked={item.data.creatives.every((c: any) => selectedCreatives.has(String(c.id)))}
                                  onChange={(e) => {
                                    const newSelected = new Set(selectedCreatives);
                                    if (e.target.checked) {
                                      item.data.creatives.forEach((c: any) => newSelected.add(String(c.id)));
                                    } else {
                                      item.data.creatives.forEach((c: any) => newSelected.delete(String(c.id)));
                                    }
                                    setSelectedCreatives(newSelected);
                                  }}
                                />
                                {item.data.releaseId ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const rel = item.data.creatives?.[0]?.releases;
                                      setAssetRelease({
                                        id: item.data.releaseId,
                                        name: item.releaseName,
                                        release_date: rel?.release_date || item.data.releaseDate,
                                        cover_url: rel?.cover_url,
                                        spotify_uri: rel?.spotify_uri,
                                      });
                                    }}
                                    className="text-sm font-medium text-white hover:underline decoration-dotted underline-offset-4 cursor-pointer"
                                    title="Show cover & Spotify URI"
                                  >
                                    {item.releaseName}
                                  </button>
                                ) : (
                                  <span className="text-sm font-medium text-white">
                                    {item.releaseName === 'No Release' ? 'No Release Assigned' : item.releaseName}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      
                      const creative = item.data;
                      return (
                      <tr 
                        key={creative.id} 
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={(e) => handleRowClick(creative, e)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap w-12" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedCreatives.has(String(creative.id))}
                            onChange={() => {}}
                            onClick={(e) => {
                              const creativeIndex = filteredCreatives.findIndex(c => c.id === creative.id);
                              const newSelected = new Set(selectedCreatives);
                              
                              if (e.shiftKey && lastSelectedIndex !== null) {
                                // Shift-click: select range
                                const start = Math.min(lastSelectedIndex, creativeIndex);
                                const end = Math.max(lastSelectedIndex, creativeIndex);
                                
                                for (let i = start; i <= end; i++) {
                                  newSelected.add(String(filteredCreatives[i].id));
                                }
                              } else {
                                // Normal click: toggle single item
                                if (newSelected.has(String(creative.id))) {
                                  newSelected.delete(String(creative.id));
                                } else {
                                  newSelected.add(String(creative.id));
                                }
                              }
                              
                              setSelectedCreatives(newSelected);
                              setLastSelectedIndex(creativeIndex);
                            }}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                          />
                        </td>
                        {/* Thumbnail column — always 9:16 (w-12 h-20). Falls
                             back through thumbnail_url → instagram_thumbnail_url
                             → tiktok_thumbnail_url so TikTok auth-code-only
                             creatives display their poster once the
                             download-tiktok-spark-ad-media edge function
                             has mirrored it into our thumbnails bucket. */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const thumbSrc =
                              creative.thumbnail_url ||
                              creative.instagram_thumbnail_url ||
                              creative.tiktok_thumbnail_url ||
                              null;
                            if (thumbSrc) {
                              return (
                                <div className="relative w-12 h-20 bg-gray-800 rounded overflow-hidden">
                                  <img
                                    src={thumbSrc}
                                    alt="Creative thumbnail"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              );
                            }
                            return (
                              <div className="w-12 h-20 bg-gray-200 dark:bg-gray-700 rounded" />
                            );
                          })()}
                        </td>
                        {/* Platform column — up to 3 icons shown in parallel
                             (File, Instagram, TikTok) based on what sources
                             the creative actually carries. File = any
                             Supabase-storage / Dropbox video. Instagram =
                             any IG reel URL linked. TikTok = any Spark Ad
                             auth code attached. */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const content = creative.content || '';
                            const hasFile =
                              (content.includes('://') && isSupabaseStorageUrl(content)) ||
                              content.includes('dropbox.com');
                            const hasInstagram =
                              !!creative.mergedInstagramReelUrl ||
                              !!creative.original_instagram_url ||
                              content.includes('instagram.com');
                            const hasTiktok =
                              !!creative.mergedTiktokAuthCode ||
                              (typeof content === 'string' && content.startsWith('#'));
                            return (
                              <div className="flex items-center gap-2">
                                {hasFile && (
                                  <HardDrive className="h-5 w-5 text-white" aria-label="Stored video file" />
                                )}
                                {hasInstagram && (
                                  <Instagram className="h-5 w-5 text-white" aria-label="Instagram" />
                                )}
                                {hasTiktok && (
                                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-white" aria-label="TikTok">
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                  </svg>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 cursor-pointer" onClick={(e) => handleMessageClick(creative, e)}>
                          <div className="space-y-1">
                            {creative.videoName ? (
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                    {creative.videoName}
                                  </span>
                                  {creative.is_trial_reel && (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400" title="This reel is a Trial Reel — only shared to non-followers, not visible in profile grid">
                                      Trial Reel
                                    </span>
                                  )}
                                  {creative.ig_author_username && (
                                    <a
                                      href={`https://instagram.com/${creative.ig_author_username}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                                    >
                                      @{creative.ig_author_username}
                                    </a>
                                  )}
                                </div>
                                {/* Post date + caption (thumbnail and URL shown elsewhere) */}
                                {!(creative.original_instagram_url && creative.mergedInstagramReelUrl === creative.content) && (
                                  (creative.ig_post_date || creative.ig_caption) && (
                                    <div className="mt-1 ml-4">
                                      {creative.ig_post_date && (
                                        <span className="block text-[10px] text-gray-400 dark:text-gray-500">
                                          {new Date(creative.ig_post_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                      )}
                                      {creative.ig_caption && (
                                        <span className="block text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2 max-w-[200px] sm:max-w-[250px]">
                                          {creative.ig_caption}
                                        </span>
                                      )}
                                    </div>
                                  )
                                )}

                                {/* Show merged Instagram URL if available */}
                                {creative.mergedInstagramReelUrl && (
                                  <div className="flex items-center gap-1 mt-1 ml-4">
                                    <span className="text-gray-400">↳</span>
                                    {isInstagramUrlNew(creative) && (
                                      <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                                    )}
                                    <Instagram
                                      className="h-4 w-4 text-white"
                                    />
                                    <a
                                      href={creative.mergedInstagramReelUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hover:underline truncate block max-w-[200px] sm:max-w-[300px] md:max-w-full text-xs text-gray-500 dark:text-gray-400"
                                      title={creative.mergedInstagramReelUrl}
                                    >
                                      {creative.mergedInstagramReelUrl}
                                    </a>
                                  </div>
                                )}
                                
                                {/* Original Instagram URL (preserved when licensed audio replaced reel with file) — link only, no label */}
                                {creative.original_instagram_url && (
                                  <div className="flex items-center gap-1 mt-1 ml-4">
                                    <span className="text-gray-400">↳</span>
                                    <a
                                      href={creative.original_instagram_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hover:underline truncate block max-w-[200px] sm:max-w-[300px] md:max-w-full text-xs text-gray-500 dark:text-gray-400"
                                      title={creative.original_instagram_url}
                                    >
                                      {creative.original_instagram_url}
                                    </a>
                                  </div>
                                )}

                                {/* Show merged TikTok auth code if available */}
                                {creative.mergedTiktokAuthCode && (
                                  <div className="flex items-center gap-1 mt-1 ml-4">
                                    <span className="text-gray-400">↳</span>
                                    {isTikTokCodeNew(creative) && (
                                      <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                                    )}
                                    <svg 
                                      viewBox="0 0 24 24" 
                                      fill="currentColor"
                                      className="h-4 w-4 text-white"
                                    >
                                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                    </svg>
                                    <span className="truncate block max-w-[200px] sm:max-w-[300px] md:max-w-full text-xs text-gray-500 dark:text-gray-400" title={creative.mergedTiktokAuthCode}>
                                      {creative.mergedTiktokAuthCode}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              creative.platform !== 'tiktok' ? (
                                creative.original_instagram_url ? (
                                  <a
                                    className="flex items-center gap-2 hover:underline"
                                    href={creative.original_instagram_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={creative.original_instagram_url}
                                  >
                                    <Instagram className="h-4 w-4 text-white" />
                                    <span className="truncate block max-w-[120px] sm:max-w-[150px] md:max-w-[200px]">
                                      {creative.original_instagram_url}
                                    </span>
                                  </a>
                                ) : (
                                  <a
                                    className="flex items-center gap-2 hover:underline"
                                    href={creative.content}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={creative.content}
                                  >
                                    {isSupabaseStorageUrl(creative.content) ? (
                                      <HardDrive className="h-4 w-4 text-white" />
                                    ) : creative.platform === 'dropbox' ? (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 528 512"
                                        className="h-4 w-4 text-gray-400"
                                      >
                                        <path fill="currentColor" d="M264.4 116.3l-132 84.3 132 84.3-132 84.3L0 284.1l132.3-84.3L0 116.3 132.3 32l132.1 84.3zM131.6 395.7l132-84.3 132 84.3-132 84.3-132-84.3zm132.8-111.6l132-84.3-132-83.6L395.7 32 528 116.3l-132.3 84.3L528 284.8l-132.3 84.3-131.3-85z"/>
                                      </svg>
                                    ) : creative.platform === 'instagram' ? (
                                      <Instagram
                                        className="h-4 w-4 text-white"
                                      />
                                    ) : (
                                      <HardDrive className="h-4 w-4 text-white" />
                                    )}
                                    <span className="truncate block max-w-[120px] sm:max-w-[150px] md:max-w-[200px]">
                                      {creative.content}
                                    </span>
                                  </a>
                                )
                              ) : (
                                <span className="flex items-center gap-2" title={creative.content}>
                                  <svg 
                                    viewBox="0 0 24 24" 
                                    fill="currentColor"
                                    className="h-4 w-4 text-white"
                                  >
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                  </svg>
                                  <span className="truncate block max-w-[120px] sm:max-w-[150px] md:max-w-[200px]">
                                    {creative.content.substring(0, 30)}...
                                  </span>
                                </span>
                              )
                            )}

                            {/* Audio-type badge — only show "Original sound" (licensed label hidden) */}
                            {creative.platform === 'instagram' && !creative.original_instagram_url && creative.audio_type && creative.audio_type !== 'licensed_music' && (
                              <div className="mt-1 ml-4 relative group/license inline-flex">
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded cursor-help text-green-600/80 bg-green-50 dark:bg-green-900/20"
                                >
                                  <Info className="h-3 w-3" />
                                  Original sound
                                </span>
                                <div className="absolute left-0 top-full mt-1 hidden group-hover/license:block z-20 w-64 bg-gray-900 text-gray-100 text-xs leading-snug rounded-md p-2 shadow-lg">
                                  This Reel uses an original sound and is eligible to be advertised as an Instagram post.
                                </div>
                              </div>
                            )}

                            {/* Replaced storage file (when licensed audio replaced the reel) — link only, no label */}
                            {creative.original_instagram_url && !creative.videoName && (
                              <div className="mt-1 ml-4">
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-400">↳</span>
                                  <a
                                    href={creative.content}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline truncate block max-w-[120px] sm:max-w-[150px] md:max-w-[200px] text-xs text-gray-500 dark:text-gray-400"
                                    title={creative.content}
                                  >
                                    {creative.content}
                                  </a>
                                </div>
                              </div>
                            )}

                            {/* Display merged Instagram URL if available (skip if it equals content, i.e. licensed-replaced) */}
                            {creative.mergedInstagramReelUrl && !creative.videoName && creative.mergedInstagramReelUrl !== creative.content && (
                              <div className="flex items-center gap-1 mt-1 ml-4">
                                <span className="text-gray-400">↳</span>
                                {isInstagramUrlNew(creative) && (
                                  <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                                )}
                                <Instagram
                                  className="h-4 w-4 text-white"
                                />
                                <a
                                  href={creative.mergedInstagramReelUrl}
                                  target="_blank"
                                  rel="noopener noreferrer" 
                                  className="hover:underline truncate block max-w-[120px] sm:max-w-[150px] md:max-w-[200px] text-xs text-gray-500 dark:text-gray-400"
                                  title={creative.mergedInstagramReelUrl}
                                >
                                  {creative.mergedInstagramReelUrl}
                                </a>
                              </div>
                            )}
                            
                            {/* Display merged TikTok auth code if available */}
                            {creative.mergedTiktokAuthCode && !creative.videoName && (
                              <div className="flex items-center gap-1 mt-1 ml-4">
                                <span className="text-gray-400">↳</span>
                                {isTikTokCodeNew(creative) && (
                                  <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                                )}
                                <svg 
                                  viewBox="0 0 24 24" 
                                  fill="currentColor"
                                  className="h-4 w-4 text-white"
                                >
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                                <span className="truncate block max-w-[120px] sm:max-w-[150px] md:max-w-[200px] text-xs text-gray-500 dark:text-gray-400" title={creative.mergedTiktokAuthCode}>
                                  {creative.mergedTiktokAuthCode}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const displayStatus = getDisplayStatus(creative);
                            return (
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(displayStatus)}`}>
                                {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                              </span>
                            );
                          })()}
                          {creative.status === 'rejected' && creative.rejectionReason && (
                            <RejectionReasonTag reason={creative.rejectionReason} />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {format(new Date(creative.createdAt), 'MMM d, yyyy')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => handleMessageClick(creative, e)}
                              className="text-white hover:text-gray-300"
                              title="View video"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // If multiple items selected, open bulk release modal
                                if (selectedCreatives.size > 1 && selectedCreatives.has(String(creative.id))) {
                                  setShowBulkReleaseModal(true);
                                } else {
                                  setEditingCreative(creative);
                                }
                              }}
                              className="text-white hover:text-gray-300"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {(() => {
                              // Show a download button whenever we have a
                              // permanently stored video file for this
                              // creative — either a direct Supabase upload
                              // (content) or a TikTok Spark Ad video that
                              // download-tiktok-spark-ad-media has mirrored
                              // into our videos bucket.
                              const downloadUrl =
                                (isSupabaseStorageUrl(creative.content || '') ? creative.content : null) ||
                                (creative as any).tiktok_post_video_stored_url ||
                                null;
                              if (!downloadUrl) return null;
                              const filename =
                                creative.videoName ||
                                (downloadUrl.split('/').pop() || 'video').split('?')[0];
                              return (
                                <a
                                  href={downloadUrl}
                                  download={filename}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-white hover:text-gray-300"
                                  title="Download video"
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              );
                            })()}
                            <button
                              onClick={(e) => handleDeleteClick(creative.id, e)}
                              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              disabled={deletingId === creative.id}
                            >
                              {deletingId === creative.id ? (
                                <Loader className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    })
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredCreatives.length)}</span> of{' '}
                      <span className="font-medium">{filteredCreatives.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-primary-50 dark:bg-primary-900 border-primary-500 text-primary-600 dark:text-primary-200'
                              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {showSubmissionModal && (
          <AdCreativeSubmissionModal 
            onClose={() => {
              setShowSubmissionModal(false);
              // Re-fetch with current filters to ensure release dividers appear
              const filters = {
                artistId: id,
                status: undefined
              };
              fetchAdCreatives(1, 1000, filters, false, isProtected ? artistClient : undefined);
            }}
            artistId={id}
            isArtistView={true}
          />
        )}

        {editingCreative && (
          <EditAdCreativeModal
            creative={editingCreative}
            onClose={() => setEditingCreative(null)}
            isArtistView={true}
          />
        )}

        {showBulkReleaseModal && id && selectedCreatives.size > 0 && (
          <BulkAdCreativeReleaseModal
            creatives={filteredCreatives.filter(c => selectedCreatives.has(String(c.id)))}
            artistId={id}
            onClose={() => setShowBulkReleaseModal(false)}
            onSuccess={() => {
              setSelectedCreatives(new Set());
              fetchArtistAdCreatives();
            }}
          />
        )}

        <ConfirmationModal
          isOpen={deleteConfirmation.isOpen}
          title="Delete Ad Creative"
          message={
            deleteConfirmation.creativeId
              ? `Are you sure you want to delete this ad creative? This action cannot be undone.`
              : ''
          }
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirmation({ isOpen: false, creativeId: null })}
          isLoading={!!deletingId}
        />

        <VideoPreviewModal
          isOpen={previewModal.isOpen}
          onClose={() => {
            setPreviewModal({ isOpen: false, videoUrl: '', videoName: '', platform: '', creativeId: '' });
            // Remove video_id from URL when modal closes
            searchParams.delete('video_id');
            setSearchParams(searchParams);
          }}
          videoUrl={previewModal.videoUrl}
          videoName={previewModal.videoName}
          platform={previewModal.platform}
          creativeId={previewModal.creativeId}
        />

        {/* Duplicate detection modal */}
        {showDuplicateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {duplicateMatches.some(m => m.similarity > 0) ? 'Potential Duplicate Creatives' : 'Merge Creatives'}
                </h3>
                <button
                  onClick={() => setShowDuplicateModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {duplicateMatches.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No duplicate creatives found
                </div>
              ) : (
                <div>
                  {/* Show Merge All button if there are multiple pairs with same target */}
                  {duplicateMatches.length > 1 && (() => {
                    const hasCommonTarget = duplicateMatches.length > 1 &&
                      duplicateMatches.every(m => m.fileCreative.id === duplicateMatches[0].fileCreative.id);
                    const isFileTarget = duplicateMatches[0].fileCreative.platform === 'direct_upload' || duplicateMatches[0].fileCreative.platform === 'dropbox';
                    if (hasCommonTarget && isFileTarget) {
                      return (
                        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                          <button
                            onClick={handleMergeAll}
                            className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm font-medium"
                          >
                            Merge All ({duplicateMatches.length} pairs)
                          </button>
                          <p className="text-xs text-green-800 dark:text-green-200 mt-2">
                            Merges all creatives into the file upload: {duplicateMatches[0].fileCreative.video_name || duplicateMatches[0].fileCreative.content?.split('/').pop() || 'Unnamed'}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  <div className="overflow-y-auto flex-1">
                    <div className="space-y-4">
                      {duplicateMatches.map((match, index) => (
                        <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {match.similarity > 0
                                ? `Similarity: ${(match.similarity * 100).toFixed(1)}%`
                                : 'Manual selection'
                              }
                            </div>
                            <button
                              onClick={() => handleMergeCreatives(match.fileCreative.id, match.instagramCreative.id)}
                              className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-sm font-medium"
                              title="Merge creatives"
                            >
                              <Copy className="h-4 w-4" />
                              Merge
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            {/* First Creative */}
                            <div className="border border-gray-300 dark:border-gray-600 rounded p-3">
                              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {match.fileCreative.platform === 'direct_upload' || match.fileCreative.platform === 'dropbox' ? 'File Upload' : match.fileCreative.platform}
                              </div>
                              <div className="text-sm text-gray-900 dark:text-white mb-1">
                                {match.fileCreative.video_name || match.fileCreative.content?.split('/').pop() || 'Unnamed'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                ID: {match.fileCreative.id}
                              </div>
                              {(match.fileCreative.thumbnail_url || match.fileCreative.instagram_thumbnail_url || match.fileCreative.tiktok_thumbnail_url) && (
                                <img
                                  src={match.fileCreative.thumbnail_url || match.fileCreative.instagram_thumbnail_url || match.fileCreative.tiktok_thumbnail_url}
                                  alt={`${match.fileCreative.platform} thumbnail`}
                                  className="w-16 h-24 object-cover rounded"
                                />
                              )}
                            </div>
                            
                            {/* Second Creative */}
                            <div className="border border-gray-300 dark:border-gray-600 rounded p-3">
                              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {match.instagramCreative.platform === 'instagram' ? 'Instagram' : match.instagramCreative.platform}
                              </div>
                              <div className="text-sm text-gray-900 dark:text-white mb-1">
                                {match.instagramCreative.video_name || match.instagramCreative.content?.split('/').pop() || 'Unnamed'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                ID: {match.instagramCreative.id}
                              </div>
                              {(match.instagramCreative.thumbnail_url || match.instagramCreative.instagram_thumbnail_url || match.instagramCreative.tiktok_thumbnail_url) && (
                                <img
                                  src={match.instagramCreative.thumbnail_url || match.instagramCreative.instagram_thumbnail_url || match.instagramCreative.tiktok_thumbnail_url}
                                  alt={`${match.instagramCreative.platform} thumbnail`}
                                  className="w-16 h-24 object-cover rounded"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowDuplicateModal(false)}
                  className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-md transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Multi-Delete Confirmation Modal */}
        {multiDeleteModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Delete Multiple Ad Creatives
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                You are about to delete <strong>{multiDeleteModal.count}</strong> ad creatives. This action cannot be undone.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                To confirm, please enter the number of ad creatives you want to delete:
              </p>
              <input
                type="number"
                placeholder={`Enter ${multiDeleteModal.count}`}
                value={multiDeleteConfirmInput}
                onChange={(e) => setMultiDeleteConfirmInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white mb-4"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setMultiDeleteModal({ isOpen: false, count: 0 });
                    setMultiDeleteConfirmInput('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMultiDelete}
                  disabled={parseInt(multiDeleteConfirmInput) !== multiDeleteModal.count || isMultiDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isMultiDeleting ? (
                    <span className="flex items-center gap-2">
                      <Loader className="h-4 w-4 animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    `Delete ${multiDeleteModal.count} Ad Creatives`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <ArtistSettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />

      {assetRelease && (
        <ReleaseAssetsModal
          release={assetRelease}
          db={artistClient}
          onClose={() => setAssetRelease(null)}
          onSaved={(patch) => setAssetRelease((prev: any) => prev ? { ...prev, ...patch } : prev)}
        />
      )}
    </div>
  );
};